// Interactive corridor map (react-leaflet v4 + OSM tiles — both already in
// package.json, previously unused; the rest of the app maps with Google Maps,
// but the Safety Dashboard mirrors the SR-2 study's Leaflet/OSM look).
//
// Receives ALREADY-FILTERED arrays from the page — no filtering here.
// CircleMarker everywhere: Leaflet's default icon PNGs 404 under CRA bundling.
import React, { useEffect, useMemo } from 'react';
import {
  MapContainer, TileLayer, LayersControl, LayerGroup, Polyline, CircleMarker,
  Circle, Popup, Tooltip, useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { findingSentence, visibleFindings } from './insightsText';

const SEVERITY_RUN_COLORS = { severe: '#c92a2a', moderate: '#e8590c', minor: '#f5a623' };
// In "All analyses" mode runs are colored by ANALYSIS instead of severity so
// the mechanisms stay tellable-apart on one map.
const ANALYSIS_RUN_COLORS = {
  SSD: '#c92a2a', PSD: '#9c36b5', GLR: '#e8a30c', FSA: '#1971c2', HCA: '#e8590c',
  GRW: '#5f3dc4', ISD: '#0ca678',
};
const CRASH_COLORS = { fatal: '#7f0000', injury: '#e03131', pdo: '#868e96', unknown: '#adb5bd' };
const CRASH_RADIUS = { fatal: 8, injury: 6, pdo: 4.5, unknown: 4.5 };

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) map.fitBounds(bounds, { padding: [30, 30] });
  }, [map, bounds]);
  return null;
}

// Row-click navigation: fly to a table row's segment/point and drop a pulse ring.
function FlyToFocus({ focus }) {
  const map = useMap();
  useEffect(() => {
    if (!focus) return;
    if (focus.bounds && focus.bounds.length > 1) {
      map.flyToBounds(focus.bounds, { padding: [60, 60], maxZoom: 16, duration: 0.8 });
    } else if (focus.lat != null) {
      map.flyTo([focus.lat, focus.lng], focus.zoom || 15, { duration: 0.8 });
    }
  }, [map, focus]);
  return null;
}

const fmtMp = (v) => (v === null || v === undefined ? '—' : v.toFixed(3));

export default function SafetyMap({ corridors, runs, crashes, clusters, intersections, analysis, focus }) {
  // corridors: {P: corridorPayload, N: corridorPayload}
  const bounds = useMemo(() => {
    const pts = Object.values(corridors || {})
      .filter(Boolean)
      .flatMap((c) => c.points.map((p) => [p[0], p[1]]));
    return pts.length ? pts : null;
  }, [corridors]);

  const isAll = analysis === 'ALL';
  const runColor = (run) => (isAll
    ? (ANALYSIS_RUN_COLORS[run.analysis] || '#c92a2a')
    : (SEVERITY_RUN_COLORS[run.worstSeverity] || SEVERITY_RUN_COLORS.minor));

  if (!bounds) return <div className="safety-map-empty">Select a corridor to load the map.</div>;

  return (
    <MapContainer bounds={bounds} className="safety-map" scrollWheelZoom>
      <FitBounds bounds={bounds} />
      <FlyToFocus focus={focus} />
      {focus && focus.lat != null && (
        <CircleMarker center={[focus.lat, focus.lng]} radius={16}
          pathOptions={{ color: '#3b82f6', weight: 3, fillOpacity: 0.08, dashArray: '2 4' }} />
      )}
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <LayersControl position="topright" collapsed={false}>
        <LayersControl.Overlay checked name="Surveyed centerline">
          <LayerGroup>
            {Object.entries(corridors || {}).map(([dir, c]) =>
              c ? (
                <Polyline
                  key={dir}
                  positions={c.points.map((p) => [p[0], p[1]])}
                  pathOptions={{ color: '#0c5c66', weight: 3, opacity: 0.45 }}
                />
              ) : null
            )}
          </LayerGroup>
        </LayersControl.Overlay>

        <LayersControl.Overlay checked name={`Deficiency runs (${analysis})`}>
          <LayerGroup>
            {(runs || []).map((run) => (
              <React.Fragment key={`${run.analysis || analysis}-${run.direction}-${run.id}`}>
                {/* white casing keeps the run legible over busy tiles */}
                <Polyline positions={run.coords}
                  pathOptions={{ color: '#ffffff', weight: 8, opacity: 0.75 }} />
                <Polyline positions={run.coords}
                  pathOptions={{ color: runColor(run), weight: 5, opacity: 0.95 }}>
                  <Tooltip sticky>
                    {isAll ? `${run.analysis} · ` : ''}
                    MP {fmtMp(run.mpStart)}–{fmtMp(run.mpEnd)} ({run.direction === 'P' ? 'EB' : 'WB'})
                    · {run.curveNumber != null
                      ? `curve #${run.curveNumber} · score ${run.worstValue}% · R=${Math.round(run.radiusFt || 0)} ft`
                      : `${run.nPoints} stations · worst ${run.worstValue}`}
                  </Tooltip>
                </Polyline>
              </React.Fragment>
            ))}
          </LayerGroup>
        </LayersControl.Overlay>

        {(intersections || []).length > 0 && (
          <LayersControl.Overlay checked name="Intersections (ISD)">
            <LayerGroup>
              {intersections.map((ix) => (
                <React.Fragment key={ix.id}>
                  {/* required ISD distance ring (AASHTO distance from the ISD docs) */}
                  {ix.isdFt && (
                    <Circle center={[ix.lat, ix.lng]} radius={ix.isdFt * 0.3048}
                      pathOptions={{ color: '#0ca678', weight: 1.5, fillOpacity: 0.04, dashArray: '3 4' }} />
                  )}
                  <CircleMarker center={[ix.lat, ix.lng]} radius={6}
                    pathOptions={{ color: '#ffffff', weight: 1.5, fillColor: '#0ca678', fillOpacity: 0.95 }}>
                    <Tooltip>
                      Intersection {ix.id || '—'} · MP {fmtMp(ix.mp)}
                      {ix.isdFt ? ` · required ISD ${Math.round(ix.isdFt)} ft` : ''}
                      {ix.quality != null ? ` · quality ${(ix.quality * 100).toFixed(0)}%` : ''}
                    </Tooltip>
                  </CircleMarker>
                </React.Fragment>
              ))}
            </LayerGroup>
          </LayersControl.Overlay>
        )}

        <LayersControl.Overlay checked name="Crashes">
          <LayerGroup>
            {(crashes || []).map((c) => (
              <React.Fragment key={c.id}>
                {/* amber halo: crash time falls inside this location's glare periods */}
                {c.glareMatch && (
                  <CircleMarker center={[c.lat, c.lon]} radius={10}
                    pathOptions={{ color: '#e8a30c', weight: 2.5, fillOpacity: 0 }} />
                )}
                <CircleMarker
                  center={[c.lat, c.lon]}
                  radius={c.isAnimal ? 4 : CRASH_RADIUS[c.severityClass]}
                  pathOptions={
                    c.isAnimal
                      ? { color: '#9aa7ac', weight: 1.6, fillOpacity: 0 }
                      : { color: '#ffffff', weight: 1.2, fillColor: CRASH_COLORS[c.severityClass], fillOpacity: 0.95 }
                  }
                >
                  <Popup>
                    <div className="safety-popup">
                      <strong>{c.manner || 'Crash'}</strong>
                      <div className="safety-popup-mp">
                        MP {fmtMp(c.milepost)} · {c.city || '—'} · {c.datetime ? c.datetime.slice(0, 16).replace('T', ' ') : '—'}
                      </div>
                      <table><tbody>
                        <tr><td>Severity</td><td>{c.severity || '—'}</td></tr>
                        <tr><td>First event</td><td>{c.firstHarmful || '—'}</td></tr>
                        <tr><td>Light</td><td>{c.light || '—'}</td></tr>
                        <tr><td>Surface</td><td>{c.surface || '—'}</td></tr>
                        <tr><td>Junction</td><td>{c.junction || '—'}</td></tr>
                        <tr><td>Vehicles</td><td>{c.numVehicles ?? '—'}</td></tr>
                        <tr><td>Nearest run</td>
                          <td>{(() => { const m = c.nearestRunMi || {}; const v = analysis === 'ALL'
                            ? Object.entries(m).sort((a, b) => a[1] - b[1])[0]
                            : (m[analysis] != null ? [analysis, m[analysis]] : null);
                          return v ? `${v[1]} mi (${v[0]})` : '—'; })()}</td></tr>
                        {c.nearestIntersectionFt != null && (
                          <tr><td>Intersection</td>
                            <td>{c.nearestIntersectionFt} ft{c.insideIsd ? ' — inside required ISD' : ''}</td></tr>
                        )}
                      </tbody></table>
                      {visibleFindings(c, analysis).length > 0 && (
                        <div className="safety-popup-findings">
                          <strong>Deficiencies at this location</strong>
                          <ul>
                            {visibleFindings(c, analysis).map(([a, f]) => (
                              <li key={a}>
                                <b>{a}</b> — {findingSentence(a, f, c)}
                                {f.reportUrl && (
                                  <> <a href={f.reportUrl} target="_blank" rel="noreferrer">Report ↗</a></>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {c.isEmbankment && <em>Embankment / overturn event (GRW signature).</em>}
                      {c.isAnimal && <em>Animal strike — excluded from the correlation verdict.</em>}
                    </div>
                  </Popup>
                </CircleMarker>
              </React.Fragment>
            ))}
          </LayerGroup>
        </LayersControl.Overlay>

        <LayersControl.Overlay checked name="Crash clusters">
          <LayerGroup>
            {(clusters || []).map((cl, i) => (
              <Circle
                key={i}
                center={[cl.lat, cl.lng]}
                radius={200 + cl.count * 60}
                pathOptions={{ color: '#6b2e8a', weight: 2.5, fillColor: '#6b2e8a', fillOpacity: 0.07, dashArray: '4 3' }}
              >
                <Tooltip>
                  {cl.count} crashes · MP {fmtMp(cl.mpStart)}
                  {cl.mpEnd > cl.mpStart ? `–${fmtMp(cl.mpEnd)}` : ''}
                </Tooltip>
              </Circle>
            ))}
          </LayerGroup>
        </LayersControl.Overlay>
      </LayersControl>
    </MapContainer>
  );
}
